// packages/api/src/hooks/useSupportTickets.ts
//
// Central de Chamados de suporte (Escopo A "balcão"). Consumido por consultor
// (abrir + acompanhar) e admin/atendente (badge + status). Permissions:
// `support:create` (abrir), `support:read` (listar/detalhe — dono ou manage),
// `support:manage` (summary + PATCH status). Contrato:
// docs/contracts/ESCR-271-support-tickets.md.
//
// - GET   /support/tickets          → { count, rows } paginado (escopo server-side)
// - GET   /support/tickets/summary  → { open, byStatus } (badge admin)
// - GET   /support/tickets/:id       → SupportTicketDetail (+ attachments)
// - POST  /support/tickets          → SupportTicketView (abre; anexo = Pattern A)
// - PATCH /support/tickets/:id       → SupportTicketDetail (só atendente)

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import type {
  ApiSuccess,
  CreateSupportTicketDto,
  PaginatedResponse,
  SupportTicketCategory,
  SupportTicketDetail,
  SupportTicketStatus,
  SupportTicketSummary,
  SupportTicketView,
  UpdateSupportTicketStatusDto,
} from '@alamo/types';

import { apiClient } from '../client';
import { supportTicketsKeys } from '../query-keys';

const BASE = '/support/tickets';

export interface SupportTicketsFilters {
  page?: number;
  limit?: number;
  status?: SupportTicketStatus;
  category?: SupportTicketCategory;
  q?: string;
}

/**
 * Lista paginada de chamados. Escopo server-side: dono vê só os próprios;
 * `support:manage` vê todos. Backend devolve `{ count, rows }`; normalizamos
 * para `PaginatedResponse<T>` (PAGINATION-STANDARD.md FE-side shape).
 */
export function useSupportTickets(
  filters?: SupportTicketsFilters,
): UseQueryResult<PaginatedResponse<SupportTicketView>, Error> {
  const page = filters?.page ?? 1;
  const limit = filters?.limit ?? 20;
  return useQuery({
    queryKey: supportTicketsKeys.list(filters as Record<string, unknown> | undefined),
    queryFn: async (): Promise<PaginatedResponse<SupportTicketView>> => {
      const res = await apiClient.get<ApiSuccess<{ count: number; rows: SupportTicketView[] }>>(
        BASE,
        { params: filters },
      );
      const raw = res.data.data;
      return {
        items: raw.rows,
        total: raw.count,
        page,
        limit,
        totalPages: Math.max(1, Math.ceil(raw.count / limit)),
      } satisfies PaginatedResponse<SupportTicketView>;
    },
  });
}

/** Detalhe de um chamado (+ anexos com URL presignada). */
export function useSupportTicket(
  id: string | null | undefined,
): UseQueryResult<SupportTicketDetail, Error> {
  return useQuery({
    queryKey: supportTicketsKeys.detail(id ?? ''),
    enabled: !!id,
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<SupportTicketDetail>>(`${BASE}/${id}`);
      return res.data.data;
    },
  });
}

/** Contagem para o badge do menu admin (`support:manage`). */
export function useSupportTicketsSummary(options?: {
  enabled?: boolean;
}): UseQueryResult<SupportTicketSummary, Error> {
  return useQuery({
    queryKey: supportTicketsKeys.summary(),
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<SupportTicketSummary>>(`${BASE}/summary`);
      return res.data.data;
    },
    enabled: options?.enabled ?? true,
  });
}

/** Abre um chamado (opener = usuário logado). Anexo via Pattern A (pós-201). */
export function useCreateSupportTicket(): UseMutationResult<
  SupportTicketView,
  Error,
  CreateSupportTicketDto
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: CreateSupportTicketDto) => {
      const res = await apiClient.post<ApiSuccess<SupportTicketView>>(BASE, dto);
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: supportTicketsKeys.all });
    },
  });
}

/** Atualiza status (+ nota) — só atendente (`support:manage`). */
export function useUpdateSupportTicketStatus(
  id: string,
): UseMutationResult<SupportTicketDetail, Error, UpdateSupportTicketStatusDto> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: UpdateSupportTicketStatusDto) => {
      const res = await apiClient.patch<ApiSuccess<SupportTicketDetail>>(`${BASE}/${id}`, dto);
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: supportTicketsKeys.all });
    },
  });
}
